import { useEffect, useRef } from 'react';
import { type WorkQueueCase } from '../../services/workQueue';

// Marker class the caller must put (via DataTable's rowClassName) on the row
// whose case id matches focusCaseId - the scroll target below.
export const FOCUS_TARGET_CLASS = 'wq-focus-target';

interface UseFocusCaseScrollOptions {
    focusCaseId: string | null;
    cases: WorkQueueCase[] | undefined;
    tbodyRef: React.RefObject<HTMLTableSectionElement>;
    onFocusHandled: () => void;
}

/**
 * One-shot scroll: when focusCaseId is set and its row is rendered from the
 * loaded list data, scrolls it into view and clears the focus id. Re-runs as
 * `cases` arrives because a search selection that switches steps renders its
 * rows a commit after the focus id is armed.
 *
 * The caseInData guard is load-bearing: the pinned-row path renders a marked
 * row for a case that is NOT in `cases` — scrolling to it and clearing the
 * focus id would immediately unpin it.
 */
export function useFocusCaseScroll({
    focusCaseId,
    cases,
    tbodyRef,
    onFocusHandled
}: UseFocusCaseScrollOptions): void {
    const lastFiredRef = useRef<string | null>(null);

    useEffect(() => {
        if (!focusCaseId) {
            // Reset so re-selecting the same patient from search scrolls again.
            lastFiredRef.current = null;
            return;
        }
        if (lastFiredRef.current === focusCaseId) return;

        const caseInData = cases?.some((c) => c.id === focusCaseId) ?? false;
        if (!caseInData) return;

        const row = tbodyRef.current?.querySelector<HTMLElement>(`tr.${FOCUS_TARGET_CLASS}`);
        if (!row) return;

        lastFiredRef.current = focusCaseId;
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        onFocusHandled();
    }, [focusCaseId, cases, tbodyRef, onFocusHandled]);
}
